import * as v from 'valibot';

import { DynamicData, Stage, TimeType } from '../common.types';
import { Documents } from '../shared/documents.types';
import { Reasons } from '../shared/reasons.types';
import { ReportStatus } from '../shared/report.types';

const ClaimStatus = v.picklist([
	// the old statuses are kept for backward compatibility
	'Draft',
	'Active',

	// Draft status
	'FNOL In Progress',
	// Active statuses
	'Pending Insured',
	'Pending Claims Handler',
	'Claim Calculating',
	'Calculation Complete',

	// Closed
	'Closed',
]);

export type ClaimStatus = v.InferOutput<typeof ClaimStatus>;

const stage: Record<ClaimStatus, Stage> = {
	Draft: 'Draft',
	'FNOL In Progress': 'Draft',

	Active: 'Active',
	'Pending Insured': 'Active',
	'Pending Claims Handler': 'Active',
	'Claim Calculating': 'Active',
	'Calculation Complete': 'Active',

	Closed: 'Closed',
};

export function getClaimStage(status: ClaimStatus): Stage {
	return stage[status];
}

export const Claim = v.pipe(
	v.object({
		claimId: v.number(),
		claimReferenceNumber: v.nullish(v.string()),
		claimCreated: v.string(),
		dateofLoss: v.nullish(v.string()),
		insured: v.nullish(v.string()),
		claimType: v.nullish(v.string()),
		policyNumber: v.nullish(v.string()),
		claimsHandler: v.nullish(v.string()),
		claimStatus: ClaimStatus,
		claimsHandlerId: v.nullish(v.string()),
		claimStatusId: v.string(),
		fileStatus: v.custom<`${number}/${number}`>(
			(input) => typeof input === 'string' && /^\d+\/\d+$/.test(input),
		),
	}),
	v.transform((data) => ({
		...data,
		stage: stage[data.claimStatus],
	})),
);

export type Claim = v.InferOutput<typeof Claim>;
export type ClaimResponse = v.InferInput<typeof Claim>;

export const ClaimsResults = v.object({
	results: v.array(Claim),
	count: v.number(),
});
export type ClaimsResults = v.InferOutput<typeof ClaimsResults>;
export type ClaimsResultsResponse = v.InferInput<typeof ClaimsResults>;

export const ClaimType = v.picklist(['Cat', 'Non-Cat']);
export type ClaimType = v.InferOutput<typeof ClaimType>;

export const ClaimFilters = v.object({
	policyReferences: v.optional(v.array(v.string())),
	claimsStatusIds: v.optional(v.array(v.string())),
	claimType: v.optional(ClaimType),
	fromDateOfLoss: v.optional(v.string()),
	toDateOfLoss: v.optional(v.string()),
	fromCreatedDate: v.optional(v.string()),
	toCreatedDate: v.optional(v.string()),
	claimsHandlerIds: v.optional(v.array(v.string())),
	searchText: v.optional(v.string()),
	hideClosed: v.optional(v.boolean()),
});

export type ClaimFilters = v.InferOutput<typeof ClaimFilters>;

export const ClaimsQuery = v.object({
	skip: v.number(),
	take: v.number(),
	...ClaimFilters.entries,
});

export type ClaimsQuery = v.InferOutput<typeof ClaimsQuery>;

const InterruptionDetails = v.object({
	dateofLoss: v.nullish(v.string()),
	isCat: v.nullish(v.boolean()),
	interruptionOnGoing: v.nullish(v.boolean()),
	incidentDescription: v.nullish(v.string()),
	estimatedPeriodofIndemnityDateType: v.nullish(TimeType),
	estimatedPeriodofIndemnity: v.nullish(v.number()),
	hasAlternativeContact: v.nullish(v.boolean()),
	calculation: v.nullish(v.string()),
	catCodeId: v.nullish(v.number()),
	causeOfLossCodeId: v.nullish(v.number()),
});

const ClaimPolicy = v.object({
	policyId: v.nullable(v.number()),
	policyNumber: v.nullable(v.string()),
	businessInterruptionLimit: v.nullable(v.boolean()),
	biLimit: v.nullish(v.number()),
	coInsurance: v.nullable(v.boolean()),
	percentage: v.nullish(v.number()),
	isMaximumLengthOfIndemnityUnlimited: v.nullable(v.boolean()),
	maximumLengthOfIndemnity: v.nullish(v.number()),
	waitingPeriod: v.nullable(v.boolean()),
	waitingPeriodDuration: v.nullish(v.number()),
	waitingPeriodDurationHours: v.nullish(v.boolean()),
	ordinaryPayrollInsured: v.nullable(v.boolean()),
	timeLimitApplied: v.nullish(v.boolean()),
	timeLimitNumber: v.nullish(v.number()),
	timeLimitDurationDays: v.nullish(v.boolean()),
	ordinaryPayrollAmounts: v.nullish(v.boolean()),
	dollarLimit: v.nullish(v.number()),
	isBusinessInterruptionSumInsured: v.nullish(v.boolean()),
	businessInterruptionSumInsured: v.nullish(v.number()),
	hasAverageClausePerPolicy: v.nullish(v.boolean()),
	isDeclarationLinkedPolicy: v.nullish(v.boolean()),
	organisationId: v.nullish(v.number()),
});

const ClaimInsured = v.object({
	claimReferenceNumber: v.nullish(v.string()),
	name: v.nullish(v.string()),
	town: v.nullish(v.string()),
	email: v.nullish(v.string()),
	stateId: v.nullish(v.string()),
	regionId: v.nullish(v.string()),
	countryId: v.nullish(v.string()),
	zipCode: v.nullish(v.string()),
	storeNumber: v.nullish(v.string()),
	addressLine1: v.nullish(v.string()),
	addressLine2: v.nullish(v.string()),
	businessName: v.nullish(v.string()),
	naicsCodeId: v.nullish(v.number()),
	phoneNumber: v.nullish(v.string()),
});
const AlternativeContact = v.nullish(
	v.object({
		alternativeContactName: v.nullish(v.string()),
		alternativeContactEmail: v.nullish(v.string()),
		alternativeContactNumber: v.nullish(v.string()),
		relationshipToInsured: v.nullish(v.string()),
		isPrimaryContact: v.nullish(v.boolean()),
	}),
);
const ClaimDetails = v.object({
	claimPolicy: v.nullish(v.partial(ClaimPolicy)),
	claimInsured: ClaimInsured,
	alternativeContact: AlternativeContact,
});

export const ClaimValues = v.object({
	claimPolicy: v.nullish(v.partial(ClaimPolicy)),
	claimInsured: ClaimInsured,
	alternativeContact: AlternativeContact,
	claim: v.object({
		...InterruptionDetails.entries,
	}),
});

export type ClaimValues = v.InferOutput<typeof ClaimValues>;

export const SingleClaim = v.pipe(
	v.object({
		claimId: v.number(),
		// when the claim is not found, we get an empty claim with no status. We can use this to show a not found message
		claimStatus: v.optional(ClaimStatus),
		claimReferenceNumber: v.nullish(v.string()),
		claimCreated: v.string(),
		insured: v.nullish(v.string()),
		insurer: v.nullish(v.string()),
		claimType: v.nullish(v.string()),
		policyNumber: v.nullish(v.string()),
		claimsHandler: v.nullish(v.string()),
		claimsHandlerId: v.nullish(v.string()),
		indemnity: v.nullish(v.string()),
		...InterruptionDetails.entries,
		calculationTypeId: v.nullish(v.string()),
		questionnaireCompleted: v.boolean(),
		fnolCompleted: v.boolean(),
		showHandlerProcess: v.boolean(),
		claimDocuments: Documents,
		claimDetails: DynamicData,
		...ClaimDetails.entries,
		optIn: v.boolean(),
		reportStatus: ReportStatus,
		isTier2Enabled: v.boolean(),
		salesVerified: v.optional(v.boolean()),
	}),
	v.transform((data) => {
		return {
			...data,
			stage: data.claimStatus ? stage[data.claimStatus] : undefined,
		};
	}),
);

export type SingleClaim = v.InferOutput<typeof SingleClaim>;

export type SingleClaimResponse = v.InferInput<typeof SingleClaim>;
export const ClaimBody = v.object({
	isDraft: v.boolean(),
	...ClaimValues.entries,
	claim: v.object({
		claimId: v.number(),
		...InterruptionDetails.entries,
		catCodeId: v.nullish(v.number()),
		causeOfLossCodeId: v.nullish(v.number()),
	}),
});

export type ClaimBody = v.InferOutput<typeof ClaimBody>;

export const PutClaimResponse = v.object({
	errors: v.nullish(
		v.array(
			v.object({
				propertyName: v.string(),
				errorMessage: v.string(),
			}),
		),
	),
});

export type PutClaimResponse = v.InferOutput<typeof PutClaimResponse>;

export const CloseClaimBody = v.object({
	closureReasonId: v.number(),
	closureReason: v.nullable(v.string()),
});

export type CloseClaimBody = v.InferOutput<typeof CloseClaimBody>;

export const ReopenClaimBody = v.object({
	reopenReasonId: v.number(),
	reopenReason: v.nullable(v.string()),
});

export type ReopenClaimBody = v.InferOutput<typeof ReopenClaimBody>;

export const VerifySalesBodySchema = v.object({
	SalesReasonType: v.optional(Reasons),
	SalesRejectionReasonId: v.number(),
	SalesRejectionReason: v.string(),
	SalesVerified: v.boolean(),
});

export type VerifySalesBody = v.InferOutput<typeof VerifySalesBodySchema>;

export const NewClaimResponse = v.object({
	claimId: v.number(),
});

export type NewClaimResponse = v.InferOutput<typeof NewClaimResponse>;

export const CalculationTypes = v.array(
	v.object({
		id: v.string(),
		name: v.string(),
	}),
);
export type CalculationTypes = v.InferOutput<typeof CalculationTypes>;

export const ClaimStatuses = v.array(
	v.object({
		id: v.string(),
		name: v.string(),
	}),
);

export type ClaimStatuses = v.InferOutput<typeof ClaimStatuses>;

export const ClaimCatCodes = v.array(
	v.object({
		id: v.number(),
		name: v.string(),
		code: v.string(),
	}),
);
export type ClaimCatCodes = v.InferOutput<typeof ClaimCatCodes>;

export const ClaimCauseOfLossCodes = v.array(
	v.object({
		id: v.number(),
		name: v.string(),
		code: v.string(),
	}),
);
export type ClaimCauseOfLossCodes = v.InferOutput<typeof ClaimCauseOfLossCodes>;

// GET /api/Claims/claim/{id}/sales-period-data
export enum ESalesPeriodType {
	PreLoss = 1,
	PostLoss = 2,
}

export const SalesPeriodTypeSchema = v.enum(ESalesPeriodType);

export enum EIntervalType {
	Daily = 1,
	Weekly = 2,
	Monthly = 3,
}

export const IntervalTypeSchema = v.enum(EIntervalType);

export const SalesDataPeriodSchema = v.object({
	id: v.number(),
	claimId: v.number(),
	salesPeriodTypeId: SalesPeriodTypeSchema,
	intervalTypeId: IntervalTypeSchema,
	salesPeriod: v.string(),
	amount: v.optional(v.number()),
	comment: v.optional(v.string()),
	isSubmitted: v.boolean(),
	isClosed: v.boolean(),
	periodStartDate: v.string(),
});

export const DailySalesDataPeriodResponseSchema = v.object({
	day: v.string(),
	periodStartDate: v.string(), // used to display items in order,
	preLossPeriodSales: v.array(
		v.object({
			periodStartDate: v.string(), // used to display items in order,
			weekRange: v.string(),
			id: v.number(), // the ID of the claimSalePeriod item,
			amount: v.optional(v.number()),
			isSubmitted: v.boolean(),
			isClosed: v.boolean(), // show "Closed" in "amount" field once closed, amount can never be changed
		}),
	),
	lossPeriodSales: v.array(
		v.object({
			periodStartDate: v.string(), // used to display items in order,
			weekRange: v.string(),
			id: v.number(), // the ID of the claimSalePeriod item,
			amount: v.optional(v.number()),
			isSubmitted: v.boolean(),
			isClosed: v.boolean(), // show "Closed" in "amount" field once closed, amount can never be changed
		}),
	),
});

export type DailySalesDataPeriodResponse = v.InferOutput<
	typeof DailySalesDataPeriodResponseSchema
>;

export const WeeklySalesDataPeriodResponseSchema = v.object({
	week: v.string(),
	periodStartDate: v.string(), // used to display items in order,
	preLossPeriodSales: v.array(
		v.object({
			periodStartDate: v.string(), // used to display items in order,
			week: v.string(),
			from: v.string(),
			to: v.string(),
			id: v.number(), // the ID of the claimSalePeriod item,
			amount: v.optional(v.number()),
			isSubmitted: v.boolean(),
			isClosed: v.boolean(), // show "Closed" in "amount" field once closed, amount can never be changed
		}),
	),
	lossPeriodSales: v.array(
		v.object({
			periodStartDate: v.string(), // used to display items in order,
			week: v.string(),
			from: v.string(),
			to: v.string(),
			id: v.number(), // the ID of the claimSalePeriod item,
			amount: v.optional(v.number()),
			isSubmitted: v.boolean(),
			isClosed: v.boolean(), // show "Closed" in "amount" field once closed, amount can never be changed
		}),
	),
});

export type WeeklySalesDataPeriodResponse = v.InferOutput<
	typeof WeeklySalesDataPeriodResponseSchema
>;

export const MonthlySalesDataPeriodResponseSchema = v.object({
	month: v.string(),
	periodStartDate: v.string(), // used to display items in order,
	preLossPeriodSales: v.array(
		v.object({
			periodStartDate: v.string(), // used to display items in order,
			year: v.string(), // e.g. "2025/2026"
			id: v.number(), // the ID of the claimSalePeriod item,
			amount: v.optional(v.number()),
			isSubmitted: v.boolean(),
			isClosed: v.boolean(), // show "Closed" in "amount" field once closed, amount can never be changed
		}),
	),
	lossPeriodSales: v.array(
		v.object({
			periodStartDate: v.string(), // used to display items in order,
			year: v.string(), // e.g. "2025/2026"
			id: v.number(), // the ID of the claimSalePeriod item,
			amount: v.optional(v.number()),
			isSubmitted: v.boolean(),
			isClosed: v.boolean(), // show "Closed" in "amount" field once closed, amount can never be changed
		}),
	),
});

export type MonthlySalesDataPeriodResponse = v.InferOutput<
	typeof MonthlySalesDataPeriodResponseSchema
>;

export const SalesDataPeriodResponseSchema = v.object({
	salesPeriodData: v.object({
		intervalType: IntervalTypeSchema,
		comments: v.optional(v.string()),
		rows: v.array(
			v.union([
				DailySalesDataPeriodResponseSchema,
				WeeklySalesDataPeriodResponseSchema,
				MonthlySalesDataPeriodResponseSchema,
			]),
		),
	}),
});

export type SalesDataPeriod = v.InferOutput<
	typeof SalesDataPeriodResponseSchema
>;

export type SalesDataPeriodResponse = v.InferOutput<
	typeof SalesDataPeriodResponseSchema
>;

export const DailyPeriodSaleSchema = v.object({
	periodStartDate: v.optional(v.string()),
	weekRange: v.string(),
	id: v.optional(v.number()),
	amount: v.optional(v.number()),
	isSubmitted: v.optional(v.boolean()),
	isClosed: v.boolean(),
});

export const DailySalesAnalysisResponseSchema = v.object({
	day: v.string(),
	periodStartDate: v.string(),
	preLossPeriodSales: v.array(DailyPeriodSaleSchema),
	lossPeriodSales: v.array(DailyPeriodSaleSchema),
});

export type DailySalesAnalysisResponse = v.InferOutput<
	typeof DailySalesAnalysisResponseSchema
>;

export const WeeklyPeriodSaleSchema = v.object({
	periodStartDate: v.string(),
	week: v.string(),
	from: v.string(),
	to: v.string(),
	id: v.number(),
	amount: v.optional(v.number()),
	hasClosure: v.boolean(),
});

export const WeeklySalesAnalysisResponseSchema = v.object({
	preLossPeriodSales: v.array(WeeklyPeriodSaleSchema),
	lossPeriodSales: v.array(WeeklyPeriodSaleSchema),
});

export type WeeklySalesAnalysisResponse = v.InferOutput<
	typeof WeeklySalesAnalysisResponseSchema
>;

export const BusinessClosedDaySchema = v.object({
	reason: v.string(),
	fromDate: v.string(),
	toDate: v.string(),
});

export type BusinessClosedDay = v.InferOutput<typeof BusinessClosedDaySchema>;

export const BusinessSeasonalitySchema = v.object({
	fromDate: v.string(),
	toDate: v.string(),
	reason: v.string(),
	comment: v.string(),
});

export type BusinessSeasonality = v.InferOutput<
	typeof BusinessSeasonalitySchema
>;

const SalesAnalysisFootnotes = {
	comments: v.optional(v.string()),
	businessClosedDays: v.optional(v.array(BusinessClosedDaySchema)),
	businessSeasonality: v.optional(v.array(BusinessSeasonalitySchema)),
};

const MonthlyPeriodSaleSchema = v.object({
	periodStartDate: v.string(),
	year: v.string(),
	id: v.number(),
	amount: v.optional(v.number()),
	isSubmitted: v.boolean(),
	isClosed: v.boolean(),
});

const MonthlySalesAnalysisResponseSchema = v.object({
	month: v.string(),
	periodStartDate: v.string(),
	preLossPeriodSales: v.array(MonthlyPeriodSaleSchema),
	lossPeriodSales: v.array(MonthlyPeriodSaleSchema),
	salesTrend: v.number(),
});

const MonthlyPeriodPriorToLossSchema = v.object({
	month: v.string(),
	periodStartDate: v.string(),
	salesTrend: v.number(),
	preLossPeriodSales: v.array(MonthlyPeriodSaleSchema),
});

export type MonthlyPeriodPriorToLossResponse = v.InferOutput<
	typeof MonthlyPeriodPriorToLossSchema
>;

export type MonthlySalesAnalysisResponse = v.InferOutput<
	typeof MonthlySalesAnalysisResponseSchema
>;

export const SalesAnalysisResponseSchema = v.object({
	salesPeriodData: v.variant('intervalType', [
		v.object({
			...SalesAnalysisFootnotes,
			intervalType: v.literal(EIntervalType.Daily),
			rows: v.array(DailySalesAnalysisResponseSchema),
		}),
		v.object({
			...SalesAnalysisFootnotes,
			intervalType: v.literal(EIntervalType.Weekly),
			weeklyAverage: v.nullable(v.number()),
			rows: v.array(WeeklySalesAnalysisResponseSchema),
		}),
		v.object({
			...SalesAnalysisFootnotes,
			intervalType: v.literal(EIntervalType.Monthly),
			rows: v.array(MonthlySalesAnalysisResponseSchema),
			periodsPriorToLoss: v.optional(v.array(MonthlyPeriodPriorToLossSchema)),
		}),
	]),
});

export type SalesAnalysisResponse = v.InferOutput<
	typeof SalesAnalysisResponseSchema
>;

export const SalesLossRowSchema = v.object({
	orderByDate: v.string(),
	displayDate: v.optional(v.string()),
	dayOfWeek: v.string(),
	projectedSales: v.number(),
	actualSales: v.number(),
	salesShortfall: v.optional(v.nullable(v.number())),
	lessWaitingPeriod: v.optional(v.nullable(v.number())),
	salesLoss: v.number(),
	isClosed: v.boolean(),
});

export const WeeklySalesLossRowSchema = v.object({
	orderByDate: v.string(),
	week: v.string(),
	dateFrom: v.optional(v.string()),
	dateTo: v.optional(v.string()),
	projectedSales: v.number(),
	actualSales: v.number(),
	salesShortfall: v.optional(v.nullable(v.number())),
	lessWaitingPeriod: v.optional(v.nullable(v.number())),
	salesLoss: v.number(),
});

export const MonthlySalesLossRowSchema = v.object({
	orderByDate: v.string(),
	month: v.string(),
	dateFromDateTime: v.optional(v.string()),
	dateToDateTime: v.optional(v.string()),
	sales: v.number(),
	projectedSales: v.number(),
	actualSales: v.number(),
	salesShortfall: v.nullable(v.number()),
	lessWaitingPeriod: v.nullable(v.number()),
	salesLoss: v.number(),
});

const SalesLossResponseBaseSchema = {
	waitingPeriod: v.nullable(v.string()),
};

export const DailySalesPeriodDataSchema = v.object({
	...SalesLossResponseBaseSchema,
	intervalType: v.literal(EIntervalType.Daily),
	rows: v.array(SalesLossRowSchema),
});

export const WeeklySalesPeriodDataSchema = v.object({
	...SalesLossResponseBaseSchema,
	intervalType: v.literal(EIntervalType.Weekly),
	rows: v.array(WeeklySalesLossRowSchema),
});

export const MonthlySalesPeriodDataSchema = v.object({
	...SalesLossResponseBaseSchema,
	intervalType: v.literal(EIntervalType.Monthly),
	// Year labels are supplied by the API, e.g. "2024" or "2024/2025".
	preLossPeriodYear: v.string(),
	lossPeriodYear: v.string(),
	// Percentage points: 11 represents an 11% trend.
	salesTrend: v.number(),
	rows: v.array(MonthlySalesLossRowSchema),
	// The API links notes to monthly rows by date; the UI assigns letters.
	footnotes: v.optional(
		v.nullable(
			v.array(
				v.object({
					orderByDate: v.nullable(v.string()),
					note: v.string(),
				}),
			),
		),
	),
});

export const SalesPeriodDataSchema = v.variant('intervalType', [
	DailySalesPeriodDataSchema,
	WeeklySalesPeriodDataSchema,
	MonthlySalesPeriodDataSchema,
]);

export type SalesLossRow = v.InferOutput<typeof SalesLossRowSchema>;
export type WeeklySalesLossRow = v.InferOutput<typeof WeeklySalesLossRowSchema>;
export type DailySalesLossResponse = v.InferOutput<
	typeof DailySalesPeriodDataSchema
>;
export type WeeklySalesLossResponse = v.InferOutput<
	typeof WeeklySalesPeriodDataSchema
>;
export type MonthlySalesLossRow = v.InferOutput<
	typeof MonthlySalesLossRowSchema
>;
export type MonthlySalesLossResponse = v.InferOutput<
	typeof MonthlySalesPeriodDataSchema
>;
export type SalesLossPeriodData = v.InferOutput<typeof SalesPeriodDataSchema>;

export const SalesLossResponseSchema = v.object({
	salesPeriodData: SalesPeriodDataSchema,
});

export type SalesLossResponse = v.InferOutput<typeof SalesLossResponseSchema>;

export const DailySalesDataPeriodRequestRowSchema = v.object({
	id: v.number(),
	amount: v.optional(v.number()),
	isSubmitted: v.boolean(),
});

export type DailySalesDataPeriodRequestRow = v.InferOutput<
	typeof DailySalesDataPeriodRequestRowSchema
>;

export const WeeklySalesDataPeriodRequestRowSchema = v.object({
	id: v.number(),
	amount: v.optional(v.number()),
	isSubmitted: v.boolean(),
	from: v.string(),
	to: v.string(),
});

export type WeeklySalesDataPeriodRequestRow = v.InferOutput<
	typeof WeeklySalesDataPeriodRequestRowSchema
>;

export const MonthlySalesDataPeriodRequestRowSchema = v.object({
	id: v.number(),
	amount: v.optional(v.number()),
	isSubmitted: v.boolean(),
});

export type MonthlySalesDataPeriodRequestRow = v.InferOutput<
	typeof MonthlySalesDataPeriodRequestRowSchema
>;

export const SalesDataPeriodRequestItemSchema = v.object({
	rows: v.array(
		v.union([
			DailySalesDataPeriodRequestRowSchema,
			WeeklySalesDataPeriodRequestRowSchema,
			MonthlySalesDataPeriodRequestRowSchema,
		]),
	),
	comments: v.optional(v.string()),
});

export type SalesDataPeriodRequestItem = v.InferOutput<
	typeof SalesDataPeriodRequestItemSchema
>;

export const SalesDataPeriodRequestSchema = v.object({
	salesPeriodData: SalesDataPeriodRequestItemSchema,
});

export type SalesDataPeriodRequest = v.InferOutput<
	typeof SalesDataPeriodRequestSchema
>;

export const SubmitSalesPeriodResponseSchema = v.object({
	salesPeriodsComplete: v.boolean(),
	salesDocumentUploaded: v.boolean(),
	errors: v.array(v.string()),
});

export type SubmitSalesPeriodResponse = v.InferOutput<
	typeof SubmitSalesPeriodResponseSchema
>;

export const SalesSubmissionPeriodResponseSchema = v.object({
	submitted: v.boolean(),
	salesPeriodsComplete: v.boolean(),
	salesDocumentUploaded: v.boolean(),
	errors: v.array(v.string()),
});

export type SalesSubmissionPeriodResponse = v.InferOutput<
	typeof SalesSubmissionPeriodResponseSchema
>;
